/** Which Azure DevOps epics mirror into the portfolio, and where they land.
 *
 * Discovery is read live from Azure DevOps every visit — the enablement rows are the
 * only stored state, so this page can never show a cached organisation that has since
 * changed. An epic here is a project in PMT; enabling one chooses the team and category
 * it files under. Everything mirrored is read-only in PMT: the teams manage their work
 * on their own boards, and this page is where the portfolio chooses what to watch.
 */

import { useState } from 'react'
import { ExternalLink, Pause, Play, RefreshCw } from 'lucide-react'

import { Button, EmptyState, ErrorState, Loading, Pill, SectionHeader } from '@/components/ui'
import { Permissions, usePermission } from '@/lib/session'
import { useTeams } from '@/features/teams/useTeams'
import {
  useAdoDiscovery,
  useAdoOverview,
  useEnableSync,
  useRunAllSyncs,
  useRunSync,
  useUpdateSync,
  type DiscoveredEpic,
  type DiscoveredProject,
  type SyncConfig,
} from '@/features/administration/useAdoSync'

const selectClass =
  'rounded border border-border bg-surface px-2 py-1 text-xs text-fg focus:outline-none'

function TeamCategoryPickers({
  team,
  categoryId,
  onTeam,
  onCategory,
  disabled,
}: {
  team: string
  categoryId: string | null
  onTeam: (team: string) => void
  onCategory: (categoryId: string | null) => void
  disabled?: boolean
}) {
  const { data } = useTeams()
  const teams = data?.teams ?? []
  const categories = teams.find((t) => t.team === team)?.categories ?? []

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <select
        className={selectClass}
        value={team}
        disabled={disabled}
        aria-label="Team"
        onChange={(event) => {
          onTeam(event.target.value)
          // A category belongs to exactly one team; carrying one across would file the
          // project under a team while pointing at another team's category.
          onCategory(null)
        }}
      >
        {teams.map((t) => (
          <option key={t.team} value={t.team}>
            {t.shortLabel}
          </option>
        ))}
      </select>
      <select
        className={selectClass}
        value={categoryId ?? ''}
        disabled={disabled || categories.length === 0}
        aria-label="Category"
        onChange={(event) => onCategory(event.target.value || null)}
      >
        <option value="">{categories.length ? 'No category' : 'No categories'}</option>
        {categories
          .filter((c) => c.isActive)
          .map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
      </select>
    </span>
  )
}

function SyncStatus({ config }: { config: SyncConfig }) {
  if (config.lastSyncError) {
    return (
      <span className="text-xs" style={{ color: 'var(--status-red)' }} title={config.lastSyncError}>
        Last sync failed
      </span>
    )
  }
  if (!config.lastSyncedAt) {
    return <span className="text-xs text-fg-muted">Waiting for first sync</span>
  }
  return (
    <span className="text-xs text-fg-muted">
      {config.lastItemCount ?? 0} items · synced{' '}
      {config.hoursSinceSync != null && config.hoursSinceSync < 0.05
        ? 'just now'
        : `${config.hoursSinceSync}h ago`}
      {config.isStale ? (
        <span className="ml-1" style={{ color: 'var(--status-amber)' }}>
          · stale
        </span>
      ) : null}
    </span>
  )
}

function EnabledSyncRow({ config, canEdit }: { config: SyncConfig; canEdit: boolean }) {
  const updateSync = useUpdateSync()
  const runSync = useRunSync()

  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="min-w-[16rem] flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-fg">{config.remoteEpicTitle || 'Untitled epic'}</span>
          {!config.enabled ? <Pill>Paused</Pill> : null}
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          <span className="text-xs text-fg-muted">{config.remoteProjectName}</span>
          <SyncStatus config={config} />
        </div>
      </div>

      <TeamCategoryPickers
        team={config.team}
        categoryId={config.categoryId}
        disabled={!canEdit || updateSync.isPending}
        onTeam={(team) => updateSync.mutate({ id: config.id, team, clearCategory: true })}
        onCategory={(categoryId) =>
          updateSync.mutate(
            categoryId
              ? { id: config.id, categoryId }
              : { id: config.id, clearCategory: true },
          )
        }
      />

      {canEdit ? (
        <span className="inline-flex items-center gap-1">
          <Button
            title="Sync now"
            disabled={runSync.isPending || !config.enabled}
            onClick={() => runSync.mutate(config.id)}
          >
            <RefreshCw size={13} aria-hidden className={runSync.isPending ? 'animate-spin' : ''} />
          </Button>
          <Button
            title={config.enabled ? 'Pause this mirror' : 'Resume this mirror'}
            disabled={updateSync.isPending}
            onClick={() => updateSync.mutate({ id: config.id, enabled: !config.enabled })}
          >
            {config.enabled ? <Pause size={13} aria-hidden /> : <Play size={13} aria-hidden />}
          </Button>
        </span>
      ) : null}
    </li>
  )
}

function DiscoveredEpicRow({
  project,
  epic,
  canEdit,
}: {
  project: DiscoveredProject
  epic: DiscoveredEpic
  canEdit: boolean
}) {
  const enableSync = useEnableSync()
  const [team, setTeam] = useState('unassigned')
  const [categoryId, setCategoryId] = useState<string | null>(null)

  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-2.5">
      <div className="min-w-[16rem] flex-1">
        <a
          href={epic.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-sm font-medium text-fg hover:underline"
        >
          {epic.title} <ExternalLink size={11} aria-hidden className="inline text-fg-muted" />
        </a>
        <span className="ml-2 text-xs text-fg-muted">{epic.state}</span>
      </div>
      {epic.syncId ? (
        <Pill>{epic.enabled ? 'Mirroring' : 'Paused'}</Pill>
      ) : canEdit ? (
        <span className="inline-flex items-center gap-2">
          <TeamCategoryPickers
            team={team}
            categoryId={categoryId}
            onTeam={setTeam}
            onCategory={setCategoryId}
            disabled={enableSync.isPending}
          />
          <Button
            disabled={enableSync.isPending}
            onClick={() =>
              enableSync.mutate({
                remoteProjectId: project.remoteProjectId,
                remoteProjectName: project.name,
                remoteEpicId: epic.remoteEpicId,
                remoteEpicTitle: epic.title,
                team,
                categoryId,
              })
            }
          >
            Enable
          </Button>
        </span>
      ) : (
        <span className="text-xs text-fg-muted">Not mirrored</span>
      )}
    </li>
  )
}

export function AdoSyncTab() {
  const canEdit = usePermission(Permissions.adminSyncEdit)
  const overview = useAdoOverview()
  const discovery = useAdoDiscovery(overview.data?.configured ?? false)
  const runAll = useRunAllSyncs()

  if (overview.isPending) return <Loading label="Reading the sync configuration" />
  if (overview.error) return <ErrorState error={overview.error} />

  if (!overview.data?.configured) {
    return (
      <EmptyState title="Azure DevOps is not connected">
        Set WPMT_ADO_ORGANIZATION_URL on the API and worker, and add the application&rsquo;s
        managed identity as a user of the DevOps organisation. Until then there is nothing to
        mirror.
      </EmptyState>
    )
  }

  const syncs = overview.data.syncs
  const enabledCount = syncs.filter((s) => s.enabled).length

  return (
    <div>
      <p className="mb-6 max-w-[75ch] text-sm text-fg-muted">
        Teams manage their work in{' '}
        <a
          href={overview.data.organizationUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="underline"
          style={{ color: 'var(--status-info)' }}
        >
          Azure DevOps
        </a>
        ; this page chooses which of their epics appear here as projects. Mirrored projects,
        sprints and items are read-only in PMT and refresh hourly — an epic&rsquo;s stories and
        bugs become work items, its iterations become sprints, and the team and category you pick
        decide where it files in the portfolio.
      </p>

      <SectionHeader
        action={
          canEdit && enabledCount > 0 ? (
            <Button
              disabled={runAll.isPending}
              onClick={() => runAll.mutate()}
              title="Run every enabled mirror now rather than waiting for the hour"
            >
              <RefreshCw size={13} aria-hidden className={runAll.isPending ? 'animate-spin' : ''} />
              Sync all now
            </Button>
          ) : undefined
        }
      >
        Mirrored epics
      </SectionHeader>

      {syncs.length === 0 ? (
        <p className="mb-6 text-sm text-fg-muted">
          Nothing is mirrored yet. Enable an epic below to bring it in as a project.
        </p>
      ) : (
        <ul className="card mb-6 divide-y divide-border">
          {syncs.map((config) => (
            <EnabledSyncRow key={config.id} config={config} canEdit={canEdit} />
          ))}
        </ul>
      )}

      <SectionHeader>Available in Azure DevOps</SectionHeader>
      {discovery.isPending ? (
        <Loading label="Reading the organisation's projects and epics" />
      ) : discovery.error ? (
        <ErrorState error={discovery.error} />
      ) : (
        (discovery.data?.projects ?? []).map((project) => (
          <div key={project.remoteProjectId} className="mb-4">
            <div className="mb-1 flex items-baseline gap-2">
              <a
                href={project.url}
                target="_blank"
                rel="noreferrer noopener"
                className="micro-label text-fg-muted hover:underline"
              >
                {project.name}
              </a>
              {project.description ? (
                <span className="truncate text-xs text-fg-muted">{project.description}</span>
              ) : null}
            </div>
            {project.epics.length === 0 ? (
              <p className="card px-4 py-2.5 text-xs text-fg-muted">
                No epics, so nothing here can mirror yet: an epic is what becomes a project in
                PMT, and its stories, issues and bugs become the work items. Create an Epic in
                this Azure DevOps project and parent the work to it — it appears here on the
                next visit, ready to enable.
              </p>
            ) : (
              <ul className="card divide-y divide-border">
                {project.epics.map((epic) => (
                  <DiscoveredEpicRow
                    key={epic.remoteEpicId}
                    project={project}
                    epic={epic}
                    canEdit={canEdit}
                  />
                ))}
              </ul>
            )}
          </div>
        ))
      )}
    </div>
  )
}
